import { Injectable } from '@angular/core';
import { OtherToolsTrashCanComponent } from 'Src/ng2/school/school-tools/other-tools/other-tools-trash-can/other-tools-trash-can.component';
import { NetworkSettingsMoreButtonComponent } from '../../../network/network-settings/network-settings-more-button/network-settings-more-button.component';
import { PortfolioPermissionDropdownComponent } from '../../modals/user-management/cluster-user/cluster-user-portfolio-modal/portfolio-permission-dropdown/portfolio-permission-dropdown.component';
import { IColumn, IColumnStuLvl, IRowData, TList } from '../../models/list-models';
import { ShelterNetworkSettingsMoreButtonComponent } from '../../../shelter/shelter-network-settings/shelter-network-settings-more-button/shelter-network-settings-more-button.component';
import { ShelterPortfolioPermissionDropdownComponent } from '../../modals/shelter-user-management/shelter-cluster-user/shelter-cluster-user-portfolio-modal/shelter-portfolio-permission-dropdown/shelter-portfolio-permission-dropdown.component';

/* istanbul ignore next */
@Injectable()
export class CellDisplayService {
  // case1 'Percent': append `%` sign to `Percent` data - JCHU
  // case2 'Flag': for cell that shows icon flag, override data value with empty string
  static formatDisplayValue({
    colVal,
    colDataType,
  }: {
    colVal: string | number;
    colDataType: string;
  }): string | number {
    switch (colDataType) {
      case 'Percent':
      case 'ShowZeroPercent':
        return colVal !== null ? `${colVal.toString()}%` : colVal;
      case 'postGres_Percent':
        const num = ((colVal as number) * 100).toFixed(1);
        return colVal !== null ? `${num}%` : colVal;
      case 'Flag':
      case 'More':
      case 'MoreInfo':
      case 'MilestoneCheckbox':
        return '';
      case 'Tooltip': // update more info to be tooltip as well
        return '';
      case 'Date':
        const momentDate = moment(colVal).format('MMM D, YYYY');
        return momentDate === 'Invalid date' ? colVal : momentDate;
      case 'Past':
        if (colVal) return colVal === '0' ? 'Perfect' : `Missing ${colVal}`;
        else return colVal;
      default:
        return colVal;
    }
  }

  // case1: add flags class to 'Flag' type data - JCHU
  static addCellValueClass({ colVal, colDataType }): string {
    switch (colDataType) {
      case 'Flag':
        switch (colVal) {
          case '1':
            return 'flags one-flag';
          case '2':
            return 'flags two-flags';
          case '0':
            return 'em-dash';
          default:
            return 'flags three-plus-flags';
        }
      case 'MoreInfo':
        switch (colVal) {
          case 'true':
            return 'center more-info';
          default:
            return 'em-dash';
        }
      case 'MilestoneCheckbox':
        switch (colVal) {
          case 'Complete':
            return 'center milestone milestone-complete';
          case 'Not due yet':
            return 'center milestone milestone-not-due-yet';
          case 'Past due':
            return 'center milestone milestone-past-due';
          case 'Due soon':
            return 'center milestone milestone-due-soon';
          default:
            return;
        }
      case 'Ellipsis':
        return 'center cell-ellipsis';
      case 'Initials':
        switch (colVal) {
          case colVal === null:
            return 'em-dash';
          default:
            return 'person-initials';
        }
      case 'ShowZeroPercent':
        switch (colVal) {
          case null:
            return 'em-dash';
          default:
            return '';
        }
      case 'NumStringWithZero':
        switch (colVal) {
          case null:
            return 'em-dash';
          default:
            return '';
        }
      case 'Past':
        return colVal === null ? 'em-dash' : colVal;
      default:
        return '';
    }
  }

  // TODO: clean this up and make it a bit and make it a bit more flexible for different lists and services
  static addCellStyleClass({ colVal, columnKey, cellConfig, data }): string {
    switch (cellConfig) {
      case 'GRADIENT':
        switch (columnKey) {
          case 'COUNT_FAILING':
            switch (+colVal) {
              case 0:
                return;
              case 1:
                return 'gradient-low';
              case 2:
              case 3:
              case 4:
                return 'gradient-medium';
              default:
                return 'gradient-high';
            }
          case 'PCT_GROWTH_ELA':
          case 'PCT_GROWTH_MATH':
            switch (+colVal) {
              case 0:
                return 'gradient-low';
              default:
                return;
            }
          case 'LATEST_REL_LEVEL_ELA':
          case 'LATEST_REL_LEVEL_MATH':
            switch (colVal) {
              case '1 below':
                return 'gradient-low';
              case '2 below':
                return 'gradient-medium';
              case '2+ below':
                return 'gradient-high';
            }
          case 'APPLIED_PATHS_FRACTION':
          case 'RESPONSES_PATHS_FRACTION':
            return this.getPostsecFractionClasses(colVal);
          case 'STUDENT_ACTIVITY_STATUS':
            return colVal === 'Needs confirmation' ? 'gradient-medium' : '';
          case 'STUDENT_ACTIVITY_HOURS':
            return this.getPostsecHoursClasses(colVal, data);
          case 'PAST_5_ATT':
          case 'PAST_10_ATT':
          case 'PAST_20_ATT':
          case 'THIS_TERM_ATT':
            return colVal === null ? '' : this.getAttAbsGradient(data);
          case 'YTD_ATTENDANCE_CHANGE':
            return this.percentChangeCurrYearGradient(colVal);
          case 'ATT_TODAY':
            return colVal === 'Absent' ? 'gradient-medium' : '';
          case 'READING_LVL_GROWTH':
            return colVal === 'Declined' || colVal === 'No growth' ? 'gradient-high' : '';
          case 'INTERACTION_LAST_5_DAYS':
            return this.remoteInteractionLast5Gradient(colVal);
          case 'INTERACTION_LAST_20_DAYS':
            return this.remoteInteractionLast20Gradient(colVal);
        }
    }
  }

  static getPostsecFractionClasses(colVal): string {
    const vals = colVal.split('/');
    if (vals[0] === '-') return '';
    else if (vals.length === 1) {
      return 'gradient-low';
    } else {
      const numerator = parseInt(vals[0]);
      const denom = parseInt(vals[1]);
      const pct = (numerator / denom) * 100;
      const a = pct === 100;
      const b = pct >= 75 && pct <= 99.9;
      const c = pct >= 50 && pct <= 74.9;
      const d = pct >= 25 && pct <= 49.9;
      const e = pct >= 0 && pct <= 24.9;
      if (a) return '';
      if (b) return 'gradient-low';
      if (c) return 'gradient-medium';
      if (d) return 'gradient-high';
      if (e) return 'gradient-higher';
    }
  }

  static getPostsecHoursClasses(colVal: string, data: string): string {
    const vals = colVal !== null ? colVal.split(' of ') : colVal;
    if ((vals === null || vals[0] === '-') && data === 'Complete') return 'gradient-medium';
    else return '';
  }

  static percentChangeCurrYearGradient(percentage: number): string {
    let gradient = '';
    if (percentage < 0) {
      gradient = 'gradient-high';
    }
    return gradient;
  }

  static getAttAbsGradient(data: { abs: number | null; timeFrame: string }): string {
    const { timeFrame, abs } = data;
    if (abs === null) {
      return '';
    } else {
      const isGradientLow =
        (timeFrame === 'last 5' && abs === 1) ||
        (timeFrame === 'last 10' && abs <= 3) ||
        (timeFrame === 'last 20' && abs <= 7) ||
        (timeFrame === 'this term' && abs <= 7);
      const isGradientMedium =
        (timeFrame === 'last 5' && abs <= 3) ||
        (timeFrame === 'last 10' && abs <= 6) ||
        (timeFrame === 'last 20' && abs <= 14) ||
        (timeFrame === 'this term' && abs <= 14);
      if (isGradientLow) {
        return 'gradient-low';
      } else if (isGradientMedium) {
        return 'gradient-medium';
      } else {
        return 'gradient-high';
      }
    }
  }

  static remoteInteractionLast5Gradient(missedStr: string): string {
    if (missedStr === null || missedStr === 'Perfect') return '';
    const [, missedNum] = missedStr.split(' ');
    const parsedNum = parseInt(missedNum);
    switch (true) {
      case parsedNum > 1:
        return 'gradient-high';
      default:
        return '';
    }
  }

  static remoteInteractionLast20Gradient(missedStr: string): string {
    if (missedStr === null || missedStr === 'Perfect') return '';
    const [, missedNum] = missedStr.split(' ');
    const parsedNum = parseInt(missedNum);
    switch (true) {
      case parsedNum > 4:
        return 'gradient-high';
      default:
        return '';
    }
  }

  // format the display value and style of rollup-table/fixed-table/infinite-table row
  // based on columnDataType(mid-lvl), columnDataFormat(stu-lvl) and special case, such as flags
  static getFormattedRowData({
    sortedRowData,
    columns,
    listType,
  }: {
    sortedRowData: IRowData[][];
    columns: IColumn[] | IColumnStuLvl[];
    listType: TList;
  }): IRowData[][] {
    const dataTypeKey = this.getDataTypeKey(listType);
    return sortedRowData.map(row => {
      return row.map((rowCol, rowColIndx) => {
        const headerColIndx = this.getRelHeaderColIndx(listType, rowColIndx);
        const colDataType = this.getColDataType({ columns, headerColIndx, dataTypeKey });
        const newRowCol = Object.assign({}, rowCol);
        let colVal: any = newRowCol.data;
        const showZeroColDataTypes = ['Past', 'ShowZeroPercent', 'NumStringWithZero'];
        // some columns display 0 when the data is 0 and others null
        if (showZeroColDataTypes.includes(colDataType) && colVal === '0') {
          colVal = 0;
        } else if (colVal === null || colVal === '0') {
          newRowCol.style = 'em-dash';
          colVal = '—';
        } else {
          if (listType !== 'ROLLUP') {
            const cellConfig = columns[rowColIndx].cellConfig;
            newRowCol.style = this.addCellValueClass({ colVal, colDataType });
            newRowCol.backgroundStyle = cellConfig
              ? this.addCellStyleClass({
                  colVal,
                  columnKey: rowCol.columnKey,
                  cellConfig,
                  data: newRowCol.meta && JSON.parse(newRowCol.meta).gradient,
                })
              : null;
          }
          newRowCol.dynamic = this.getDynamicComponent(newRowCol.meta);
        }
        // sets 'originalData' for data that is replaced with icons
        // this allows the original data to be exported via CSV (JE)
        const formattedVal =
          colVal === '—'
            ? '—'
            : this.formatDisplayValue({
                colVal,
                colDataType,
              });
        if (formattedVal === '') newRowCol.originalData = newRowCol.data;
        newRowCol.data = formattedVal;

        return newRowCol;
      });
    });
  }

  static getColDataType({ columns, headerColIndx, dataTypeKey }): string {
    let colDataType = '';
    if (headerColIndx > -1 && columns[headerColIndx]) {
      const isFlagType = columns[headerColIndx].graphQlKey.match('FLAGS');
      if (isFlagType) {
        colDataType = 'Flag';
      } else if (columns[headerColIndx][dataTypeKey]) {
        colDataType = columns[headerColIndx][dataTypeKey];
      }
    }
    return colDataType;
  }

  static getDataTypeKey(listType: TList): 'columnDataFormat' | 'columnDataType' {
    let dataTypeKey: 'columnDataFormat' | 'columnDataType';
    switch (listType) {
      case 'NETWORK':
      case 'STUDENT':
        dataTypeKey = 'columnDataFormat';
        break;
      case 'ROLLUP':
        return 'columnDataType';
      case 'SHEETS':
        return 'columnDataType';
      default:
        dataTypeKey = 'columnDataType';
        break;
    }
    return dataTypeKey;
  }

  static getRelHeaderColIndx(listType: TList, rowColIndx: number): number {
    switch (listType) {
      case 'NETWORK':
      case 'SHEETS':
      case 'STUDENT':
      case 'USER':
      case 'USER_SCHOOL_PORTFOLIO':
        return rowColIndx;
      case 'ROLLUP':
        return rowColIndx - 1;
      default:
        return rowColIndx - 1;
    }
  }
  // meta

  static getDynamicComponent(style: string | { dynamicColType: string }): any {
    let parsedStyle;
    if (!style || typeof style === 'string') {
      parsedStyle = style;
    } else {
      parsedStyle = style.dynamicColType;
    }

    let component;
    switch (parsedStyle) {
      case 'MORE':
        component = NetworkSettingsMoreButtonComponent;
        break;
      case 'SHELTER_MORE':
        component = ShelterNetworkSettingsMoreButtonComponent;
        break;
      case 'EDITABLE_DROPDOWN':
        component = PortfolioPermissionDropdownComponent;
        break;
      case 'SHELTER_EDITABLE_DROPDOWN':
        component = ShelterPortfolioPermissionDropdownComponent;
        break;
      case 'TRASH':
        component = OtherToolsTrashCanComponent;
      default:
        break;
    }
    return component;
  }
}
